import { CLIP_PREVIEW_Z } from '@src/components/content-components/clipPreviewZ';
import { usePlayerSpaceShortcut } from '@src/hooks/shortcuts/usePlayerSpaceShortcut';
import { useScrubberDrag } from '@src/hooks/useScrubberDrag';
import { DEFAULT_FRAMERATE } from '@src/utility/timecode';
import { timeFormat } from '@src/utility/timeFormat';
import React, { useCallback, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';

import CirclePlayFilledIcon from '../../svgs/CirclePlayFilledIcon';
import ForwardToEndIcon from '../../svgs/ForwardToEndIcon';
import { FullScreenIconAlt } from '../../svgs/FullScreenIcon';
import PauseBarsIcon from '../../svgs/PauseBarsIcon';
import PlayForwardIcon from '../../svgs/PlayForwardIcon';
import VerticalLine from '../../svgs/VerticalLine';
import {
  PlayBackward,
  PlayButton,
  PlayForward,
  Time,
} from '../common/PlaybackControls';
import { PlayerVolumeControl } from '../common/PlayerVolumeControl';
import { COMPACT_TOOLTIP_STYLE } from '../common/tooltipStyles';
import { ClipCommentMarkers } from './ClipCommentMarkers';
import { buildRailBlocks } from './sceneStripBlocks';
import { ScrubberHoverPreview } from './ScrubberHoverPreview';

import type { ClipCommentMarker } from './ClipCommentMarkers';
import type { PlaybackState } from './hooks/usePlaybackState';
import type { ClipCardSpan, RailBlock } from './sceneStripBlocks';
import type { Scene } from '@src/editor/models/layout/Scene';
import type { PlaybackBridge } from '@src/hooks/PlaybackBridge';
import type { PlayerVolumeControls } from '@src/hooks/usePlayerVolume';
import type { EditorElement } from '@src/utility/sceneAnchors';

const SEEK_STEP_SECONDS = 15;
const DEBOUNCE_MS = 50;

/** The painted bar at rest — the rail's own box height. */
const SEGMENT_HEIGHT = 4;
/**
 * Every segment, once the pointer is anywhere on the rail. This is the state
 * that answers "when do the scenes happen" — the structure appears as you
 * approach the bar, before you have aimed at any part of it. Without it the
 * cuts stay hidden until you sweep across them (PLAT-11504).
 */
const SEGMENT_RAIL_HOVER_HEIGHT = 6;
/**
 * The segment under the cursor, which is how the bar answers "which scene am I
 * about to seek into" without a second row of chrome (PLAT-11504).
 */
const SEGMENT_HOVER_HEIGHT = 10;
const SEGMENT_RADIUS = 2;
/** The scene break: the bar's background shows through it. */
const SEGMENT_SEAM_WIDTH = 2;

/**
 * How much of one segment is played, as a percentage of that segment's own
 * width. Both inputs are percentages of the whole rail.
 */
function segmentFillPct(
  segment: { leftPct: number; widthPct: number },
  progressPct: number,
): number {
  if (segment.widthPct <= 0) return 0;
  const playedWithin = progressPct - segment.leftPct;
  return Math.max(0, Math.min(100, (playedWithin / segment.widthPct) * 100));
}

type Props = {
  playbackState: PlaybackState | null;
  isReady: boolean;
  play: () => void;
  pause: () => void;
  setTime: (time: number) => void;
  setSeekTime: (time: number | null) => void;
  setIsSeeking: (value: boolean) => void;
  framerate?: number;
  onToggleFullscreen?: () => void;
  isFullscreen?: boolean;
  /** Pinned comments to mark beneath the video, ordered as shown in the list. */
  commentMarkers?: ClipCommentMarker[];
  /**
   * Called when a comment marker is clicked (in addition to seeking), so the
   * host can reveal the conversation — switch to the Comments tab, and show that
   * comment's drawing if it has one.
   */
  onCommentJump?: (commentId?: string) => void;
  /**
   * Persist a comment marker's new timestamp after a drag. Omit to render the
   * markers read-only.
   */
  onCommentMove?: (id: string, time: number) => void;
  /**
   * Mux asset for the scrubber's hover thumbnails — the clip's OWN rendered
   * asset, so its timeline matches the rail. Omit to show hover timecodes
   * without a frame (a clip that has never been published).
   */
  thumbnailPlaybackId?: string;
  /**
   * Master volume/mute state + actions (from `usePlayerVolume`). Omit to
   * render the bar without a volume control.
   */
  volumeControls?: PlayerVolumeControls;
  /**
   * Scenes of the clip as currently edited. The rail is cut into one segment
   * per scene, the way a player shows chapters (PLAT-11504). Edit Mode only —
   * on a published render the app's scenes describe a different video
   * (PLAT-11236). Omit to draw the rail as one unbroken bar.
   */
  sceneStrip?: {
    scenes: readonly Scene[];
    elements: readonly EditorElement[];
    /**
     * Stretches the scenes do not cover — intro and end cards — so the rail can
     * draw and name them instead of breaking off. Omit to draw them unnamed.
     */
    cards?: readonly ClipCardSpan[];
  };
  /**
   * PLAT-11657 — the bar docked far narrower than the clip preview ever is
   * (the Translation Review's 360px player column): drop the total time so the
   * play cluster stays centred and nothing crowds the fullscreen control.
   */
  compact?: boolean;
};

/**
 * Transport controls for the clip preview, rendered below the iframe canvas.
 * Visual language mirrors `BottomItems` in `src/components/stage/Stage.tsx` —
 * three-column bar with the scrubber pulled above.
 */
export const ClipPreviewControlBar = ({
  playbackState,
  isReady,
  play,
  pause,
  setTime,
  setSeekTime,
  setIsSeeking,
  framerate = DEFAULT_FRAMERATE,
  onToggleFullscreen,
  isFullscreen = false,
  commentMarkers,
  onCommentJump,
  onCommentMove,
  thumbnailPlaybackId,
  volumeControls,
  sceneStrip,
  compact = false,
}: Props) => {
  const currentTime = playbackState?.currentTime ?? 0;
  const duration = playbackState?.duration ?? 0;
  const isPlaying = playbackState?.isPlaying ?? false;
  const seekTime = playbackState?.seekTime ?? null;
  const isSeeking = playbackState?.isSeeking ?? false;

  // Assemble a PlaybackBridge from the individual props so we can
  // reuse `useScrubberDrag` (shared with safe-zone overlays).
  const bridge: PlaybackBridge | undefined = useMemo(
    () => ({
      isPlaying,
      currentTime,
      duration,
      isReady,
      seekTime,
      isSeeking,
      setSeekTime,
      setIsSeeking,
      play,
      pause,
      setTime,
    }),
    [
      isPlaying,
      currentTime,
      duration,
      isReady,
      seekTime,
      isSeeking,
      setSeekTime,
      setIsSeeking,
      play,
      pause,
      setTime,
    ],
  );

  const {
    scrubberRef,
    displayTime,
    progressPct,
    scrubberInteractive,
    onMouseDown: onScrubberMouseDown,
    computeTimeFromClientX,
  } = useScrubberDrag(bridge, { debounceMs: DEBOUNCE_MS });

  /**
   * Position under the cursor while hovering the rail, driving the frame +
   * timecode preview. `null` when not hovering.
   */
  const [hover, setHover] = useState<{
    time: number;
    clientX: number;
    railTop: number;
  } | null>(null);

  /**
   * Pointer is over the rail. Tracked separately from `hover`, which is nulled
   * during a drag and while seeking — reusing it would collapse the segments
   * in the middle of a scrub, exactly when the structure matters most.
   */
  const [railHovered, setRailHovered] = useState(false);

  const onTimelineMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Any button held means a drag is under way — the rail's own (where the
      // fill already tracks the cursor) or a comment marker's, which renders
      // its own timecode label. A second floating readout would just compete.
      if (!scrubberInteractive || isSeeking || e.buttons !== 0) {
        setHover(null);
        return;
      }
      const time = computeTimeFromClientX(e.clientX);
      if (time == null) return;
      const railTop = scrubberRef.current?.getBoundingClientRect().top;
      if (railTop == null) return;
      setHover({ time, clientX: e.clientX, railTop });
    },
    [scrubberInteractive, isSeeking, computeTimeFromClientX, scrubberRef],
  );

  /**
   * The rail's segments — one per scene, plus one per stretch the scenes leave
   * uncovered (an intro or end card). Resolved once for both the bar that draws
   * them and the hover card that names the one under the cursor.
   *
   * A clip with no scenes is one segment spanning the whole rail, so the bar
   * has a single code path and reads exactly as it always has.
   */
  const railBlocks = useMemo(
    () =>
      sceneStrip
        ? buildRailBlocks(
            sceneStrip.scenes,
            sceneStrip.elements,
            duration,
            sceneStrip.cards,
          )
        : [],
    [sceneStrip, duration],
  );

  const segments: RailBlock[] = useMemo(
    () =>
      railBlocks.length > 0
        ? railBlocks
        : [
            {
              id: 'whole-clip',
              kind: 'scene' as const,
              start: 0,
              end: duration,
              leftPct: 0,
              widthPct: 100,
            },
          ],
    [railBlocks, duration],
  );

  /**
   * The segment under the cursor. A clip with no scenes has exactly one, so the
   * bar still answers a hover — it just has nothing to name.
   */
  /**
   * `isSeeking` keeps the bar raised through a drag that has left the rail —
   * the drag continues on window listeners, and collapsing mid-scrub would
   * take the structure away at the moment it is being used.
   */
  const railEngaged = railHovered || isSeeking;

  const hoveredSegment = hover
    ? segments.find(
        segment => hover.time >= segment.start && hover.time < segment.end,
      )
    : undefined;

  const togglePlay = useCallback(() => {
    if (!isReady) return;
    if (isPlaying) pause();
    else play();
  }, [isReady, isPlaying, play, pause]);

  const jumpTo = useCallback(
    (time: number) => {
      if (!isReady || duration <= 0) return;
      const clamped = Math.max(0, Math.min(duration, time));
      const wasPlaying = isPlaying;
      setTime(clamped);
      if (wasPlaying) setTimeout(() => play(), 50);
    },
    [isReady, duration, isPlaying, setTime, play],
  );

  /**
   * Seek while a comment marker is being dragged. Deliberately not `jumpTo`:
   * that re-plays 50ms later if playback was running, which would fight the
   * drag frame by frame. Pauses once on the first move so the frame under the
   * dot is the frame the user is actually choosing.
   */
  const scrubPreview = useCallback(
    (time: number) => {
      if (!isReady || duration <= 0) return;
      if (isPlaying) pause();
      setTime(Math.max(0, Math.min(duration, time)));
    },
    [isReady, duration, isPlaying, pause, setTime],
  );

  // Stands down under a backdrop: a dialog opened from inside the preview (the
  // clips picker, a confirm) covers the player this would toggle.
  usePlayerSpaceShortcut(togglePlay);

  const atStart = displayTime <= 0;
  const atEnd = duration > 0 && displayTime >= duration;
  const curHasHours = displayTime > 3600;

  return (
    <Wrapper $fullscreen={isFullscreen} data-testid="clip-preview-control-bar">
      <Scrubber
        ref={scrubberRef}
        data-testid="clip-preview-scrubber"
        onMouseDown={onScrubberMouseDown}
        onMouseMove={onTimelineMouseMove}
        onMouseEnter={() => setRailHovered(true)}
        onMouseLeave={() => {
          setRailHovered(false);
          setHover(null);
        }}
        data-rail-hovered={railEngaged ? 'true' : undefined}
        $interactive={scrubberInteractive}
      >
        {segments.map((segment, index) => (
          <Segment
            key={segment.id}
            data-testid="clip-scene-segment"
            data-scene-id={segment.id}
            data-block-kind={segment.kind}
            data-hovered={
              segment.id === hoveredSegment?.id ? 'true' : undefined
            }
            $card={segment.kind === 'card'}
            $hovered={segment.id === hoveredSegment?.id}
            $railHovered={railEngaged}
            style={{
              left: `${segment.leftPct}%`,
              // The seam is the gap before the *next* segment, so the last one
              // keeps its full width and the bar ends flush with the rail.
              width:
                index === segments.length - 1
                  ? `${segment.widthPct}%`
                  : `calc(${segment.widthPct}% - ${SEGMENT_SEAM_WIDTH}px)`,
            }}
          >
            {/* Each segment fills on its own, so the played edge lands in the
                right place inside the right segment rather than being one bar
                painted over the seams. */}
            <SegmentFill
              style={{
                width: `${segmentFillPct(segment, progressPct)}%`,
              }}
            />
          </Segment>
        ))}
      </Scrubber>
      {hover && (
        <ScrubberHoverPreview
          time={hover.time}
          clientX={hover.clientX}
          railTop={hover.railTop}
          framerate={framerate}
          playbackId={thumbnailPlaybackId}
          sceneLabel={hoveredSegment?.label}
        />
      )}
      {commentMarkers && (
        <ClipCommentMarkers
          markers={commentMarkers}
          duration={duration}
          framerate={framerate}
          onJump={(time, commentId) => {
            jumpTo(time);
            onCommentJump?.(commentId);
          }}
          onMove={onCommentMove}
          onScrubPreview={scrubPreview}
        />
      )}
      <BottomItems>
        <Side>
          {volumeControls && (
            <PlayerVolumeControl
              {...volumeControls}
              disabled={!isReady}
              // A 360px dock: the slider still has to leave the play cluster
              // its centre.
              sliderWidth={compact ? 40 : undefined}
            />
          )}
        </Side>
        <Middle>
          <SkipBoundButton
            as={PlayBackward}
            disabled={!isReady || atStart}
            onClick={() => jumpTo(0)}
            aria-label="Skip to start"
          >
            <ForwardToEndIcon width="18" height="14" />
          </SkipBoundButton>
          <ShuttleButton
            as={PlayBackward}
            disabled={!isReady || atStart}
            onClick={() => jumpTo(displayTime - SEEK_STEP_SECONDS)}
            aria-label={`Back ${SEEK_STEP_SECONDS} seconds`}
          >
            <PlayForwardIcon width="18" height="14" />
          </ShuttleButton>
          <PlayButton
            role="button"
            background={false}
            aria-label={isPlaying ? 'Pause' : 'Play'}
            onClick={togglePlay}
          >
            {isPlaying ? (
              <PauseBarsIcon
                width="32"
                height="32"
                fillColor="var(--foreground)"
              />
            ) : (
              <CirclePlayFilledIcon
                width="32"
                height="32"
                fillColor="var(--foreground)"
              />
            )}
          </PlayButton>
          <ShuttleButton
            as={PlayForward}
            disabled={!isReady || atEnd}
            onClick={() => jumpTo(displayTime + SEEK_STEP_SECONDS)}
            aria-label={`Forward ${SEEK_STEP_SECONDS} seconds`}
          >
            <PlayForwardIcon width="18" height="14" />
          </ShuttleButton>
          <SkipBoundButton
            as={PlayForward}
            disabled={!isReady || atEnd}
            onClick={() => jumpTo(duration)}
            aria-label="Skip to end"
          >
            <ForwardToEndIcon width="18" height="14" />
          </SkipBoundButton>
        </Middle>
        <RightSide>
          <ClipTime curHasHours={curHasHours} $compact={compact}>
            <span className="current">
              {timeFormat(displayTime, framerate)}
            </span>
            {!compact && (
              <TotalTime>
                <VerticalLine />
                <span className="end">
                  {timeFormat(duration, framerate, duration > 3600)}
                </span>
              </TotalTime>
            )}
          </ClipTime>
          {onToggleFullscreen && (
            <FullscreenButton
              type="button"
              onClick={onToggleFullscreen}
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              data-tooltip-id="clip-fullscreen-tooltip"
              data-tooltip-content={
                isFullscreen ? 'Exit fullscreen' : 'Fullscreen'
              }
            >
              <FullScreenIconAlt fill="var(--foreground)" />
            </FullscreenButton>
          )}
          {ReactDOM.createPortal(
            <Tooltip
              id="clip-fullscreen-tooltip"
              style={COMPACT_TOOLTIP_STYLE}
              place="top"
              positionStrategy="fixed"
            />,
            document.body,
          )}
        </RightSide>
      </BottomItems>
    </Wrapper>
  );
};

export default ClipPreviewControlBar;

const Wrapper = styled.div<{ $fullscreen?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  box-sizing: border-box;
  padding: 8px 12px 8px;
  background-color: var(--surface-elevated);
  border-radius: 0 0 10px 10px;
  position: relative;
  z-index: 1;
  container-type: inline-size;

  ${props =>
    props.$fullscreen &&
    css`
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      width: auto;
      z-index: ${CLIP_PREVIEW_Z.modalChrome};
      border-radius: 0;
      padding: 6px 12px 10px;
    `}
`;

const ShuttleButton = styled.span`
  display: inline-flex;

  @container (max-width: 1400px) {
    display: none;
  }
`;

const SkipBoundButton = styled.span`
  display: inline-flex;

  @container (max-width: 420px) {
    display: none;
  }
`;

const TotalTime = styled.span`
  display: inline-flex;
  align-items: center;
  gap: inherit;

  @container (max-width: 520px) {
    display: none;
  }
`;

const ClipTime = styled(Time)<{ $compact: boolean }>`
  justify-content: flex-end;
  padding-right: 0;
  /* Compact (the review's 360px dock): two steps down so the time clears the
     skip control and reads as secondary to the picture (PLAT-11683). */
  font-size: ${props => (props.$compact ? '10px' : 'var(--text-xs)')};

  /* Natural width, never shrunk: an end-aligned span that shrinks below its
     text spills that text LEFT, under its neighbour (PLAT-11657). */
  .current {
    min-width: 0;
    flex-shrink: 0;
    justify-content: flex-end;
  }

  .end {
    min-width: 0;
    flex-shrink: 0;
  }
`;

/**
 * A fixed-height positioning context for the segments, which are what is
 * actually painted. Deliberately still 4px: `ControlBarSpacer` in
 * `SocialClipShareModal` hardcodes this bar's height (8 + 4 + 6 + 36 + 8), so
 * the rail's box must not change size.
 *
 * `overflow: visible` is what lets the hovered segment grow past it — the
 * growth is paint, never layout, so no hover can reflow the bar.
 */
const Scrubber = styled.div<{ $interactive: boolean }>`
  position: relative;
  width: auto;
  height: ${SEGMENT_HEIGHT}px;
  margin: 0 -12px;
  overflow: visible;
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
`;

/**
 * One scene of the clip, one card between scenes, or the whole clip where there
 * are no scenes. The gap between segments is the break — the control bar's own
 * background shows through it, so the break reads as a cut in the bar rather
 * than a drawn line.
 *
 * Centred on the rail's midline and sized by `height`, so growing on hover
 * expands symmetrically and moves nothing else.
 */
const Segment = styled.div<{
  $card: boolean;
  $hovered: boolean;
  $railHovered: boolean;
}>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  overflow: hidden;
  /* A card stretch is part of the clip and seeks like any other, but it is not
     a scene — the dimmer track says so without a second colour to learn. */
  background: ${({ $card }) =>
    $card
      ? 'color-mix(in srgb, var(--text-muted) 55%, transparent)'
      : 'var(--text-muted)'};
  border-radius: ${SEGMENT_RADIUS}px;
  transition: height 100ms ease-out;
  /* The rail owns the pointer: seeking is one behaviour along the whole bar,
     and per-segment hit targets would put seams in the middle of it. */
  pointer-events: none;

  /* All three states in ONE declaration, deliberately. Expressing the rail's
     state as \`&:hover ${'${Segment}'}\` on the parent would be a descendant selector
     (0,2,0) and outrank the hovered-segment class (0,1,0), silently capping the
     segment under the cursor at the rail-hover height. */
  height: ${({ $hovered, $railHovered }) =>
    $hovered
      ? SEGMENT_HOVER_HEIGHT
      : $railHovered
        ? SEGMENT_RAIL_HOVER_HEIGHT
        : SEGMENT_HEIGHT}px;
`;

/**
 * How much of *this* segment has played. Painting each segment's own progress
 * keeps the played edge inside the segment it belongs to, instead of one bar
 * drawn over the seams.
 */
const SegmentFill = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: var(--foreground);
`;

const BottomItems = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const Side = styled.div`
  display: flex;
  justify-content: flex-start;
  flex: 1;
  gap: 2px;
`;

const RightSide = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex: 1;
  gap: 8px;
`;

const FullscreenButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 4px;
  margin: 0;
  cursor: pointer;
  color: var(--foreground);
  border-radius: 4px;
  opacity: 0.85;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }
`;

const Middle = styled.div`
  display: flex;
  justify-content: center;
  gap: 4px;
  align-items: center;
  flex: 1;
  position: relative;
  min-width: 0;
`;
